/**
 * TipTapEditor — wiederverwendbare WYSIWYG-Editor-Komponente.
 *
 * Konfiguration:
 *   - StarterKit (Paragraph, Bold, Italic, BulletList, OrderedList, Heading, History)
 *   - Placeholder (konfigurierbar via Prop)
 *   - VariableChipExtension (D-05: atomare Variable-Chips)
 *   - Image (externe URLs, fuer Logo in Signaturen)
 *
 * onChange wird auf jeden Edit (debounce nicht noetig — TipTap macht das intern).
 * onChange empfaengt das ROHE TipTap-HTML. Sanitization passiert beim Save-Klick
 * im aufrufenden Component (siehe TemplateEditorV2).
 *
 * Tab-Wechsel-Pitfall (RESEARCH Pitfall 5): key={templateKey} im Parent setzen,
 * damit der Editor bei Template-Wechsel komplett re-mounted.
 *
 * Bild-Einfuegen: Toolbar-Button oeffnet window.prompt() fuer URL.
 * Nur https:// erlaubt (kein data: oder javascript: — XSS-Schutz).
 * Gmail-Hinweis: Bild kann erst nach User-Klick "Bilder anzeigen" sichtbar sein.
 */
import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { api } from '@libero/core/services/api';
import { VariableChipExtension } from './VariableChipExtension';
import { LineHeightExtension, LINE_HEIGHT_PRESETS } from './LineHeightExtension';
import { FontSizeExtension, FONT_SIZE_PRESETS } from './FontSizeExtension';

// Phase 8 Iter 4 (30.05.): Mail-safe Schriftarten. Gmail/Outlook fallen sonst
// auf sans-serif zurueck. Stack-Form damit jede Plattform was passendes findet.
export const FONT_FAMILY_PRESETS = [
  { label: 'Standard', value: '' }, // EMAIL_CSS-Default (Helvetica/Arial)
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Courier', value: '"Courier New", Courier, monospace' },
] as const;

// Farben fuer Schrift + Hintergrund. Default leer = unset (faellt auf EMAIL_CSS zurueck).
export const TEXT_COLORS = [
  { label: 'Schwarz', value: '#1A1A1A' },
  { label: 'Grau', value: '#555555' },
  { label: 'Rot', value: '#C0392B' },
  { label: 'Gruen', value: '#27AE60' },
  { label: 'Blau', value: '#0066CC' },
  { label: 'CI-Gelb', value: '#E9FF57' },
] as const;

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /**
   * Wenn true: Bild-Buttons werden gerendert (Datei-Upload + URL-Eingabe).
   * Nur fuer Signatur-Editor sinnvoll, Template-Editor laesst das aus.
   */
  enableImage?: boolean;
}

export function TipTapEditor({ content, onChange, placeholder, enableImage }: TipTapEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const editor = useEditor({
    extensions: [
      // StarterKit: konfiguriert + heading nur Level 2 erlaubt (mehr braucht Mail nicht)
      StarterKit,
      Placeholder.configure({ placeholder: placeholder ?? 'Vorlage eingeben…' }),
      VariableChipExtension,
      LineHeightExtension,
      // Phase 8 Iter 4 (30.05.): Rich-Text-Extensions fuer vollwertigen Mail-Editor.
      // textStyle ist die Basis fuer Color + FontSize + FontFamily — muss vor den
      // Mark-Erweiterungen geladen werden.
      TextStyle.configure({
        // mergeNestedSpanStyles=true → mehrere textStyles (z.B. color + fontSize) werden
        // in ein <span style="color:X;font-size:Y"> gemerget statt verschachtelt.
        mergeNestedSpanStyles: true,
      }),
      FontSizeExtension,
      FontFamily,
      Color,
      Underline,
      Link.configure({
        openOnClick: false, // im Editor nicht clickbar — sonst kommt der User raus
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      // Tabellen-Support (Phase 8 Iter 3, 30.05.) — sauberer als Tab-Einrueckung
      // mit Spaces, weil Mail-Clients <table> zuverlaessig rendern. Tab-Taste in
      // einer Tabelle navigiert automatisch zwischen Zellen (TipTap-Default).
      Table.configure({
        resizable: false, // resizing in Email-HTML eh nicht uebertragbar
        HTMLAttributes: {
          style: 'border-collapse:collapse;border:1px solid #DAD9D9;',
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          style: 'border:1px solid #DAD9D9;padding:6px 10px;background-color:#F5F5F5;font-weight:600;text-align:left;',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          style: 'border:1px solid #DAD9D9;padding:6px 10px;vertical-align:top;',
        },
      }),
      Image.configure({
        // inline:true → <img> lebt im <p>-Block (statt eigener Block-Node).
        //   Wichtig damit ProseMirror das <img> beim Reparse aus dem gespeicherten
        //   HTML wiedererkennt (sonst landet ein nacktes <img> ausserhalb von <p>
        //   und wird beim Parse verworfen).
        inline: true,
        // allowBase64:true → data:image/...-URLs werden beim parseHTML akzeptiert.
        //   Default ist false → TipTap ignoriert das <img> beim Initial-Render von
        //   gespeichertem HTML mit data:-src → Bild verschwindet nach Reload.
        allowBase64: true,
        // Groesse wird ueber die Toolbar-Buttons (½ / − / + / Original) gesetzt — sie
        // schreiben das width-Attribut aufs <img> (sanitizeEditorHtml erlaubt width/height).
        // Das eingebaute Drag-Resize (resize:true) ist bewusst NICHT aktiv: seine
        // NodeView setzt pointerEvents:none bis img.onload feuert und braucht CSS fuer
        // die Handles, das das Paket nicht mitliefert → Bild war nicht anklickbar.
        // max-width:100% cappt nur das Sprengen des 600px-Mail-Layouts; die per Button
        // gesetzte Breite bleibt darunter wirksam. height:auto haelt das Seitenverhaeltnis,
        // wenn nur width gesetzt ist. Inline-CSS bleibt nach juice-Inlining erhalten.
        HTMLAttributes: {
          style: 'max-width:100%;height:auto;display:inline-block;',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        // Phase 8 Iter 4 (30.05.) — WYSIWYG-Match Editor ↔ Mail:
        // Editor-Container imitiert den 600px-Mail-Wrapper (renderEmailHtml.ts)
        // mit gleicher Schriftart, -groesse, -farbe, Zeilenabstand, Padding.
        // Damit sieht die Vorlage so aus wie die spaeter gesendete Mail.
        // Tailwind-arbitrary-properties fuer Inline-Mail-Styles.
        class:
          'min-h-[250px] mx-auto max-w-[600px] bg-white border border-[#DAD9D9] rounded ' +
          'px-6 py-5 focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] ' +
          'leading-[1.6] text-[15px] text-[#1A1A1A] ' +
          '[font-family:Helvetica,Arial,sans-serif] ' +
          '[&_p]:my-[0_0_16px] [&_p]:mb-4 ' +
          // Iter 5+6: Link erbt Text-Farbe (Default schwarz) statt fix Blau.
          // `!` (Tailwind important) ist Pflicht — alte DB-Daten haben evtl.
          // `<a style="color:#0066CC">` und Browser-Default-Blau muss explicit
          // ausgehebelt werden. text-decoration ebenfalls important damit
          // Underline mit-faerbt.
          '[&_a]:!text-inherit [&_a]:!underline ' +
          '[&_strong]:font-bold [&_em]:italic ' +
          '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 ' +
          '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 ' +
          '[&_li]:mb-1 ' +
          '[&_blockquote]:ml-6 [&_blockquote]:pl-3 [&_blockquote]:border-l-[3px] ' +
          '[&_blockquote]:border-[#DAD9D9] [&_blockquote]:text-[#555555] ' +
          '[&_table]:border-collapse [&_table]:border [&_table]:border-[#DAD9D9] ' +
          '[&_td]:border [&_td]:border-[#DAD9D9] [&_td]:p-[6px_10px] [&_td]:align-top ' +
          '[&_th]:border [&_th]:border-[#DAD9D9] [&_th]:p-[6px_10px] [&_th]:bg-[#F5F5F5] [&_th]:font-semibold ' +
          '[&_hr]:my-4 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-[#DAD9D9] ' +
          // Iter 7: Variable-Chips NUR im Editor gelb hervorheben (nicht in Mail)
          '[&_span[data-variable]]:inline-block [&_span[data-variable]]:px-[6px] ' +
          '[&_span[data-variable]]:py-[1px] [&_span[data-variable]]:rounded ' +
          '[&_span[data-variable]]:bg-[#E9FF57] [&_span[data-variable]]:text-[#1A1A1A] ' +
          '[&_span[data-variable]]:text-[12px] [&_span[data-variable]]:font-mono ' +
          '[&_span[data-variable]]:select-none',
      },
    },
  });

  // BugFix 4 (Bug-Session 29.05.): content-prop-Sync — TipTap rendert content
  // nur beim Initial-Mount aus dem Constructor. Wenn der Parent ein neues
  // content liefert (z.B. nach Save+Reload, oder bei Profil-Wechsel ohne
  // key-Reset), bleibt der Editor auf dem alten Stand. setContent erzwingt
  // den Sync. `false` als 2. Argument verhindert dass das ein onUpdate
  // triggert und damit eine Endlos-Schleife oder versehentliches Mark-Dirty.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === content) return;
    editor.commands.setContent(content, false);
  }, [editor, content]);

  function insertVariable(varName: string) {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent({ type: 'variableChip', attrs: { variableName: varName } })
      .run();
  }

  function insertImageByUrl() {
    if (!editor) return;
    const url = window.prompt(
      'Bild-URL eingeben (nur https://, kein data:):\n\nBeispiel:\nhttps://www.anstoss24.de/img/teamshops-logo.png',
    );
    if (!url) return;
    if (!/^https:\/\//i.test(url)) {
      alert('Nur https://-URLs erlaubt (XSS-Schutz).');
      return;
    }
    editor.chain().focus().setImage({ src: url }).run();
  }

  /**
   * Groesse des aktuell ausgewaehlten Bildes setzen.
   *  - 'half'  → aktuelle (gerenderte) Breite halbieren
   *  - 'reset' → width/height entfernen → Originalgroesse (durch max-width:100% gecappt)
   *  - number  → absolute Breite in px (fuer +/- Schritte)
   * Schreibt das width-Attribut; height bleibt null → Browser haelt das Seitenverhaeltnis.
   */
  function setImageWidth(mode: 'half' | 'shrink' | 'grow' | 'reset') {
    if (!editor || !editor.isActive('image')) return;
    const { selection } = editor.state;
    const attrs = editor.getAttributes('image');

    // Aktuell wirksame Breite ermitteln: erst width-Attribut, sonst gerenderte DOM-Breite.
    let current = Number(attrs.width);
    if (!current || Number.isNaN(current)) {
      const dom = editor.view.nodeDOM(selection.from) as HTMLElement | null;
      const img = dom?.nodeName === 'IMG' ? (dom as HTMLImageElement) : dom?.querySelector?.('img');
      current = img ? Math.round(img.getBoundingClientRect().width) : 200;
    }

    let next: number | null;
    if (mode === 'reset') next = null;
    else if (mode === 'half') next = Math.max(24, Math.round(current / 2));
    else if (mode === 'shrink') next = Math.max(24, Math.round(current * 0.8));
    else next = Math.round(current * 1.25);

    editor.chain().focus().updateAttributes('image', { width: next, height: null }).run();
  }

  /**
   * Bild als Base64-data:-URI direkt in den Editor einbetten — KEIN Server-Upload.
   *
   * Warum nicht Server-Upload (vorher gebaut, dann verworfen):
   *   - Core mountet alle /api/programmes/*-Routes mit authenticateToken (app.ts:305).
   *     Selbst public-deklarierte GET-Routes sind dadurch auth-gated. Mail-Empfaenger
   *     (Gmail-CDN ohne Session-Cookie) kriegen HTTP 401.
   *   - Cross-Origin-Resource-Policy: same-origin verhindert externes Laden zusaetzlich.
   *
   * Warum Base64 OK:
   *   - data:-URI ist Teil des Mail-HTML — kein externes Laden noetig.
   *   - Gmail rendert data:image/* ohne "Bilder anzeigen"-Klick (im Gegensatz zu externen URLs).
   *   - Limit 200 KB pre-base64 (~270 KB nach Encoding) — bleibt unter Gmail's 102 KB
   *     Mail-Body-Clipping wenn nur 1 Bild + Text. Bei groesseren Bildern Warnung.
   *
   * Trade-off: Mail-Body waechst, jedes Send dupliziert das Bild im Body.
   * Fuer ~20 Mails/Woche und ~30-50 KB Logos voellig ok.
   */
  function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error('FileReader-Fehler'));
      reader.readAsDataURL(file);
    });
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!editor) return;
    const file = event.target.files?.[0];
    event.target.value = ''; // reset damit gleicher File nochmal triggert
    if (!file) return;
    // BugFix 29.05.: Backend-Upload statt Base64 — Gmail strippt Base64-Bilder
    // im Empfaenger-Postfach. Server-gehostete URLs werden ohne Sonderbehandlung
    // geladen. Limit hier 250 KB (Backend lehnt grossere ab).
    if (file.size > 250 * 1024) {
      setUploadError(
        `Datei zu gross (${Math.round(file.size / 1024)} KB). Max 250 KB.`,
      );
      return;
    }
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      setUploadError(`Dateityp nicht erlaubt (${file.type}). Erlaubt: PNG, JPEG, GIF, WebP.`);
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      // api ist named-Export aus @libero/core/services/api (Top-of-File-Import).
      // Endpoint braucht view:leadmanagement-teamshops permission, Cookie wird
      // automatisch mitgesendet. Returnt { success, data: { url, filename, size } }.
      const res = await api.post<{ success: boolean; data: { url: string; filename: string; size: number }; error?: any }>(
        '/programmes/leadmanagement-teamshops/signature-images',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const relativeUrl = res.data?.data?.url;
      if (!relativeUrl) {
        throw new Error('Backend hat keine URL geliefert');
      }
      // Absolute URL fuer Mail — Mail-Clients koennen relative URLs nicht aufloesen.
      // window.location.origin = z.B. https://dev.libero-anstoss24.de
      const absoluteUrl = new URL(relativeUrl, window.location.origin).toString();
      editor.chain().focus().setImage({ src: absoluteUrl }).run();
    } catch (err: any) {
      const detail = err?.response?.data?.error?.message ?? err?.message ?? 'Upload fehlgeschlagen';
      setUploadError(detail);
    } finally {
      setUploading(false);
    }
  }

  // Toolbar-Button-Klassen — aktiv (gelb) / inaktiv (grau)
  const tbBtn = (active: boolean) =>
    'px-2 py-1 text-xs rounded transition-colors ' +
    (active
      ? 'bg-[#E9FF57] text-[#1A1A1A] font-semibold'
      : 'bg-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-300');

  return (
    <div>
      {/* Format-Toolbar oberhalb des Editors — WYSIWYG-Standardfunktionen */}
      {editor && (
        <div className="mb-2 flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={tbBtn(editor.isActive('bold'))}
            title="Fett (Cmd+B)"
            aria-label="Fett"
          >
            <strong>F</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={tbBtn(editor.isActive('italic'))}
            title="Kursiv (Cmd+I)"
            aria-label="Kursiv"
          >
            <em>K</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={tbBtn(editor.isActive('underline'))}
            title="Unterstrichen (Cmd+U)"
            aria-label="Unterstrichen"
          >
            <span className="underline">U</span>
          </button>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          {/* Schriftart-Dropdown (Iter 4) */}
          <select
            className="px-2 py-1 text-xs rounded bg-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-300 transition-colors"
            value={editor.getAttributes('textStyle').fontFamily ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) editor.chain().focus().unsetFontFamily().run();
              else editor.chain().focus().setFontFamily(val).run();
            }}
            title="Schriftart fuer den markierten Text"
            aria-label="Schriftart"
          >
            {FONT_FAMILY_PRESETS.map((f) => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          {/* Schriftgroesse-Dropdown (Iter 4) */}
          <select
            className="px-2 py-1 text-xs rounded bg-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-300 transition-colors"
            value={editor.getAttributes('textStyle').fontSize ?? ''}
            onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
            title="Schriftgroesse (px) fuer den markierten Text"
            aria-label="Schriftgroesse"
          >
            {FONT_SIZE_PRESETS.map((s) => (
              <option key={s.label} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {/* Textfarbe (Iter 4) — Dropdown statt input[type=color] damit Buttons in Toolbar einheitlich aussehen */}
          <select
            className="px-2 py-1 text-xs rounded bg-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-300 transition-colors"
            value={editor.getAttributes('textStyle').color ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) editor.chain().focus().unsetColor().run();
              else editor.chain().focus().setColor(val).run();
            }}
            title="Textfarbe fuer den markierten Text"
            aria-label="Textfarbe"
          >
            <option value="">Farbe: Standard</option>
            {TEXT_COLORS.map((c) => (
              <option key={c.value} value={c.value}>
                Farbe: {c.label}
              </option>
            ))}
          </select>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          <button
            type="button"
            // Bug C (30.05.): toggleBulletList toggelt nur innerhalb der Selection.
            // Wenn der User in einer geordneten Liste ist und Aufzaehlung klickt,
            // wandelt TipTap automatisch um. Wenn der User die Liste komplett raus
            // will → `¶`-Button mit clearNodes() benutzen.
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={tbBtn(editor.isActive('bulletList'))}
            title="Aufzaehlung (zum Abschalten: ¶-Button)"
            aria-label="Aufzaehlung"
          >
            • Liste
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={tbBtn(editor.isActive('orderedList'))}
            title="Nummerierte Liste (zum Abschalten: ¶-Button)"
            aria-label="Nummerierte Liste"
          >
            1. Liste
          </button>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={tbBtn(editor.isActive('heading', { level: 2 }))}
            title="Ueberschrift"
            aria-label="Ueberschrift"
          >
            H
          </button>
          <button
            type="button"
            // Bug C (30.05.): clearNodes() vor setParagraph() — ohne dieses Aufheben
            // bleibt ein umlaufender Block-Wrapper wie `<ol>`, `<ul>` oder `<h2>` aktiv
            // und die Liste laesst sich "nicht abschalten". clearNodes() drueckt die
            // Selection zurueck auf simplen Paragraph-Modus.
            onClick={() =>
              editor.chain().focus().clearNodes().setParagraph().unsetAllMarks().run()
            }
            className={tbBtn(
              editor.isActive('paragraph')
                && !editor.isActive('bulletList')
                && !editor.isActive('orderedList')
                && !editor.isActive('heading'),
            )}
            title="Normaler Text (hebt Liste/Ueberschrift auf)"
            aria-label="Normaler Text"
          >
            ¶
          </button>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          {/* Ausrichtung (Iter 4) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={tbBtn(editor.isActive({ textAlign: 'left' }))}
            title="Linksbuendig"
            aria-label="Linksbuendig"
          >
            ⇤
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={tbBtn(editor.isActive({ textAlign: 'center' }))}
            title="Zentriert"
            aria-label="Zentriert"
          >
            ⇔
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={tbBtn(editor.isActive({ textAlign: 'right' }))}
            title="Rechtsbuendig"
            aria-label="Rechtsbuendig"
          >
            ⇥
          </button>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          {/* Link (Iter 4) */}
          <button
            type="button"
            onClick={() => {
              const prev = editor.getAttributes('link').href ?? '';
              const url = window.prompt('Link-URL (leer = Link entfernen):', prev);
              if (url === null) return; // Cancel
              if (url === '') {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                return;
              }
              const final = /^https?:\/\//i.test(url) ? url : `https://${url}`;
              editor.chain().focus().extendMarkRange('link').setLink({ href: final }).run();
            }}
            className={tbBtn(editor.isActive('link'))}
            title="Link einfuegen / bearbeiten (markierten Text verlinken)"
            aria-label="Link"
          >
            🔗
          </button>
          {/* Horizontale Linie (Iter 4) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className={tbBtn(false)}
            title="Horizontale Trennlinie einfuegen"
            aria-label="Trennlinie"
          >
            ─
          </button>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          {/* Zeilenabstand-Preset-Dropdown (Phase 8 Iter 3 Feature 2) */}
          <select
            className="px-2 py-1 text-xs rounded bg-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-300 transition-colors"
            value={
              LINE_HEIGHT_PRESETS.find(
                (p) => p.value === (editor.getAttributes('paragraph').lineHeight ?? ''),
              )?.value ?? ''
            }
            onChange={(e) => editor.chain().focus().setLineHeight(e.target.value).run()}
            title="Zeilenabstand der aktuellen Zeile/Liste"
            aria-label="Zeilenabstand"
          >
            {LINE_HEIGHT_PRESETS.map((p) => (
              <option key={p.label} value={p.value}>
                Zeilenabstand: {p.label}
              </option>
            ))}
          </select>
          <span className="w-px bg-[#DAD9D9] mx-1" aria-hidden="true" />
          {/* Tabellen-Toolbar (Phase 8 Iter 3 Feature 1) — Insert immer, Cell-Ops nur in Tabelle */}
          {!editor.isActive('table') ? (
            <button
              type="button"
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 2, withHeaderRow: false })
                  .run()
              }
              className={tbBtn(false)}
              title="Tabelle einfuegen (3 Zeilen, 2 Spalten — Tab in Zelle navigiert)"
              aria-label="Tabelle einfuegen"
            >
              ⊞ Tabelle
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => editor.chain().focus().addRowAfter().run()}
                className={tbBtn(false)}
                title="Zeile unter aktueller einfuegen"
              >
                + Zeile
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                className={tbBtn(false)}
                title="Spalte rechts einfuegen"
              >
                + Spalte
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().deleteRow().run()}
                className={tbBtn(false)}
                title="Aktuelle Zeile loeschen"
              >
                − Zeile
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().deleteColumn().run()}
                className={tbBtn(false)}
                title="Aktuelle Spalte loeschen"
              >
                − Spalte
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().deleteTable().run()}
                className={tbBtn(false)}
                title="Ganze Tabelle entfernen"
              >
                ⊠ Tabelle weg
              </button>
            </>
          )}
        </div>
      )}
      <EditorContent editor={editor} />
      {/* Bild-Groesse — erscheint nur wenn ein Bild angeklickt/ausgewaehlt ist.
          Setzt das width-Attribut; Seitenverhaeltnis bleibt erhalten (height:auto). */}
      {editor && enableImage && editor.isActive('image') && (
        <div className="mt-2 flex flex-wrap items-center gap-1 rounded bg-[#E9FF57]/20 border border-[#E9FF57] px-2 py-1">
          <span className="text-xs font-semibold text-[#1A1A1A] mr-1">Bild-Groesse:</span>
          <button type="button" onClick={() => setImageWidth('half')} className={tbBtn(false)} title="Auf die Haelfte verkleinern">
            ½ kleiner
          </button>
          <button type="button" onClick={() => setImageWidth('shrink')} className={tbBtn(false)} title="Schrittweise kleiner">
            − kleiner
          </button>
          <button type="button" onClick={() => setImageWidth('grow')} className={tbBtn(false)} title="Schrittweise groesser">
            + groesser
          </button>
          <button type="button" onClick={() => setImageWidth('reset')} className={tbBtn(false)} title="Originalgroesse">
            Original
          </button>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        <span className="text-xs text-gray-500 mr-1">Variablen einfuegen:</span>
        {['verein_name', 'verein_contact_person', 'verein_email', 'signature', 'actor_name'].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => insertVariable(v)}
            className="px-2 py-0.5 text-xs font-mono bg-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-300 transition-colors"
          >
            {`{{${v}}}`}
          </button>
        ))}
        {enableImage && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="ml-2 px-2 py-0.5 text-xs bg-[#E9FF57] text-[#1A1A1A] rounded hover:bg-[#d4ea4d] transition-colors disabled:opacity-50"
              title="Bild vom Mac einbetten (PNG/JPEG/GIF/WebP, max 200 KB — direkt in Mail-HTML eingebunden)"
            >
              {uploading ? 'einbetten…' : '+ Bild einfuegen'}
            </button>
            <button
              type="button"
              onClick={insertImageByUrl}
              disabled={uploading}
              className="px-2 py-0.5 text-xs bg-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
              title="Bild via https-URL einfuegen (extern gehostet)"
            >
              + Bild-URL
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </>
        )}
      </div>
      {uploadError && (
        <p className="mt-1 text-xs text-red-600">{uploadError}</p>
      )}
    </div>
  );
}
